<template>
  <div class="employee-register" :class="{ 'nav-collapsed': isNavCollapsed }">
    <Navigation @nav-toggle="handleNavToggle" />

    <!-- Main Content -->
    <div class="dashboard-content">
      <div class="page-container">
        <!-- Modern page header -->
        <div class="modern-page-header">
          <div class="header-content">
            <div class="title-section">
              <h1 class="dashboard-title">Employee Management</h1>
              <p class="dashboard-subtitle">
                <span class="icon">👥</span>
                Comprehensive employee database management system
              </p>
            </div>
            <div class="header-actions">
              <div class="view-selector">
                <button class="view-btn" :class="{ 'active': viewMode === 'grid' }" @click="viewMode = 'grid'">
                  <i class="bx bx-grid-alt"></i> Grid
                </button>
                <button class="view-btn" :class="{ 'active': viewMode === 'list' }" @click="viewMode = 'list'">
                  <i class="bx bx-list-ul"></i> List
                </button>
              </div>
              <button class="refresh-btn" @click="refreshData" :disabled="loading">
                <i class="bx bx-refresh" :class="{ 'spin': loading }"></i>
                {{ loading ? 'Loading...' : 'Refresh' }}
              </button>
            </div>
          </div>
          
          <div class="breadcrumb-section">
            <div class="breadcrumb">
              <span class="breadcrumb-item" @click="goToHome">
                <span class="icon">🏠</span>
                <span class="text">Home</span>
              </span>
              <span class="breadcrumb-separator">/</span>
              <span class="breadcrumb-current">Employee Register</span>
            </div>
          </div>
        </div>

        <!-- Stats Cards -->
        <div class="metrics-grid">
          <!-- Total Employees -->
          <div class="metric-card gradient-1" @click="filterByType('')">
            <div class="metric-icon">
              <i class="bx bx-group"></i>
            </div>
            <div class="metric-content">
              <p class="metric-label">Total Employees</p>
              <h2 class="metric-value">{{ formatNumber(totalItems) }}</h2>
              <div class="metric-footer">
                <span class="trend-indicator positive" v-if="newEmployeesThisMonth > 0">
                  <i class="bx bx-up-arrow-alt"></i>
                  <span>+{{ newEmployeesThisMonth }} new this month</span>
                </span>
              </div>
            </div>
          </div>

          <!-- Active Employees -->
          <div class="metric-card gradient-2" @click="filterByStatus('active')">
            <div class="metric-icon">
              <i class="bx bx-user-check"></i>
            </div>
            <div class="metric-content">
              <p class="metric-label">Active Employees</p>
              <h2 class="metric-value">{{ formatNumber(activeEmployees) }}</h2>
              <div class="metric-footer">
                <span class="trend-badge active">{{ ((activeEmployees / totalItems) * 100).toFixed(1) }}% active</span>
              </div>
            </div>
          </div>

          <!-- Permanent Staff -->
          <div class="metric-card gradient-3" @click="filterByType('Permanent')">
            <div class="metric-icon">
              <i class="bx bx-briefcase"></i>
            </div>
            <div class="metric-content">
              <p class="metric-label">Permanent Staff</p>
              <h2 class="metric-value">{{ formatNumber(permanentEmployees) }}</h2>
              <div class="metric-footer">
                <span class="trend-badge permanent">Full-time employees</span>
              </div>
            </div>
          </div>

          <!-- Departments -->
          <div class="metric-card gradient-4" @click="showDepartmentsModal">
            <div class="metric-icon">
              <i class="bx bx-building"></i>
            </div>
            <div class="metric-content">
              <p class="metric-label">Departments</p>
              <h2 class="metric-value">{{ formatNumber(departments.length) }}</h2>
              <div class="metric-footer">
                <span class="trend-indicator neutral">
                  <i class="bx bx-category"></i>
                  <span>{{ getTopDepartments(3) }}</span>
                </span>
              </div>
            </div>
          </div>
        </div>

        <!-- Quick Stats -->
        <div class="quick-stats">
          <div class="stats-grid">
            <div class="stat-item" @click="filterByType('Contract')">
              <div class="stat-icon">
                <i class="bx bx-file"></i>
              </div>
              <div class="stat-content">
                <h4>{{ contractEmployees }}</h4>
                <p>Contract Staff</p>
              </div>
            </div>
            <div class="stat-item" @click="filterByType('Internship')">
              <div class="stat-icon">
                <i class="bx bx-graduation"></i>
              </div>
              <div class="stat-content">
                <h4>{{ internshipEmployees }}</h4>
                <p>Internships</p>
              </div>
            </div>
            <div class="stat-item" @click="goToArchive">
              <div class="stat-icon">
                <i class="bx bx-archive-in"></i>
              </div>
              <div class="stat-content">
                <h4>Archive</h4>
                <p>Separated employees</p>
              </div>
            </div>
            <div class="stat-item">
              <div class="stat-icon">
                <i class="bx bx-time"></i>
              </div>
              <div class="stat-content">
                <h4>{{ averageTenure }} yrs</h4>
                <p>Average Tenure</p>
              </div>
            </div>
          </div>
        </div>

        <!-- Filters and Actions -->
        <div class="filters-section">
          <div class="filters-header">
            <h3><i class="bx bx-filter-alt"></i> Filter & Search</h3>
            <div class="filters-header-actions">
              <button
                type="button"
                class="privacy-toggle-btn"
                :class="{ active: sensitiveDataVisible }"
                @click="toggleSensitiveReveal"
                :title="sensitiveDataVisible ? 'Hide payroll no., ID, DOB, email and phone' : 'Show full payroll no., ID, DOB, email and phone'"
              >
                <i class="bx" :class="sensitiveDataVisible ? 'bx-hide' : 'bx-show'"></i>
                {{ sensitiveDataVisible ? 'Hide sensitive data' : 'Show sensitive data' }}
              </button>
              <div class="selected-count" v-if="selectedEmployees.length > 0">
                {{ selectedEmployees.length }} selected
                <button class="clear-selection" @click="clearSelection">
                  <i class="bx bx-x"></i>
                </button>
              </div>
            </div>
          </div>
          
          <div class="filters-grid">
            <!-- Search Box -->
            <div class="search-box">
              <i class="bx bx-search"></i>
              <input 
                v-model="search" 
                @input="debounceSearch"
                type="text" 
                placeholder="Search by name, ID, email, phone, or department..."
                :class="{'is-loading': loading}"
              >
              <button v-if="search" @click="clearSearch" class="clear-btn">
                <i class="bx bx-x"></i>
              </button>
            </div>

            <!-- Department Filter -->
            <div class="filter-group">
              <label><i class="bx bx-building"></i> Department</label>
              <select v-model="departmentFilter" @change="onFilterChange" class="form-select">
                <option value="">All Departments</option>
                <option v-for="dept in departments" :key="dept.id" :value="dept.id">
                  {{ dept.departmentName }}
                </option>
              </select>
            </div>

            <!-- Status Filter -->
            <div class="filter-group">
              <label><i class="bx bx-user-circle"></i> Status</label>
              <select v-model="statusFilter" @change="onFilterChange" class="form-select">
                <option value="">All Status</option>
                <option value="active">Active</option>
                <option value="resigned">Resigned</option>
              </select>
            </div>

            <!-- Type Filter -->
            <div class="filter-group">
              <label><i class="bx bx-briefcase"></i> Employment Type</label>
              <select v-model="typeFilter" @change="onFilterChange" class="form-select">
                <option value="">All Types</option>
                <option value="Permanent">Permanent</option>
                <option value="Contract">Contract</option>
                <option value="Temporary">Temporary</option>
                <option value="Internship">Internship</option>
              </select>
            </div>
          </div>

          <!-- Action Buttons -->
          <div class="action-buttons">
            <button @click="downloadEmployees" class="action-btn export" :disabled="loading || employees.length === 0">
              <i :class="isDownloading ? 'bx bx-loader-alt bx-spin' : 'bx bx-export'"></i>
              {{ isDownloading ? 'Exporting...' : 'Export' }}
            </button>
            
            <button @click="goToBulkUpload" class="action-btn upload">
              <i class="bx bx-upload"></i>
              Bulk Upload
            </button>

            <button @click="goToArchive" class="action-btn archive">
              <i class="bx bx-archive-in"></i>
              Archive
            </button>
            <button type="button" @click.prevent="openDirectOnboarding" class="action-btn secondary" style="background-color: #6c757d; color: white;">
              <i class="bx bx-mail-send"></i>
              Send Onboarding
            </button>

            <button @click="goToCreateEmployee" class="action-btn primary">
              <i class="bx bx-plus"></i>
              Add Employee
            </button>
          </div>
        </div>

        <!-- Employees Table/Grid -->
        <div class="employees-section">
          <div v-if="loading" class="loading-state">
            <div class="spinner"></div>
            <p>Loading employee data...</p>
          </div>

          <div v-else-if="employees.length === 0" class="empty-state">
            <div class="empty-icon">
              <i class="bx bx-user-x"></i>
            </div>
            <h3>No employees found</h3>
            <p>Try adjusting your search or filters</p>
            <button @click="resetFilters" class="btn btn-outline">Reset Filters</button>
          </div>

          <!-- Grid View -->
          <div v-else-if="viewMode === 'grid'" class="employees-grid">
            <div v-for="employee in employees" :key="employee.id" class="employee-card">
              <div class="card-header">
                <div class="employee-avatar" :class="getStatusClass(employee.status)">
                  {{ getInitials(employee.firstName, employee.lastName) }}
                </div>
                <div class="employee-actions">
                  <button @click="viewDetails(employee)" class="icon-btn" title="View">
                    <i class="bx bx-show"></i>
                  </button>
                  <button @click="editEmployee(employee)" class="icon-btn" title="Edit">
                    <i class="bx bx-edit"></i>
                  </button>
                  <div class="dropdown">
                    <button class="icon-btn dropdown-toggle">
                      <i class="bx bx-dots-vertical-rounded"></i>
                    </button>
                    <div class="dropdown-menu">
                      <a @click="viewUserAccount(employee)" href="javascript:void(0)" class="dropdown-item">
                        <i class="bx bx-user-circle"></i> Manage Account
                      </a>
                      <a href="javascript:void(0)" class="dropdown-item">
                        <i class="bx bx-file"></i> Generate Report
                      </a>
                      <div class="dropdown-divider"></div>
                      <a @click="deleteEmployee(employee)" href="javascript:void(0)" class="dropdown-item text-danger">
                        <i class="bx bx-trash"></i> Delete
                      </a>
                    </div>
                  </div>
                </div>
              </div>
              
              <div class="card-body">
                <h4>{{ getFullName(employee) }}</h4>
                <p class="employee-id">{{ displayEmpNo(employee) }}</p>
                <p v-if="employee.nationalId" class="privacy-meta">
                  <i class="bx bx-id-card"></i> {{ displayNationalId(employee) }}
                </p>
                <p v-if="employee.dateOfBirth" class="privacy-meta">
                  <i class="bx bx-cake"></i> {{ displayDateOfBirth(employee) }}
                </p>
                
                <div class="employee-info">
                  <div class="info-item">
                    <i class="bx bx-envelope"></i>
                    <span>{{ displayEmail(employee) }}</span>
                  </div>
                  <div class="info-item">
                    <i class="bx bx-phone"></i>
                    <span>{{ displayPhone(employee) }}</span>
                  </div>
                  <div class="info-item">
                    <i class="bx bx-building"></i>
                    <span>{{ employee.departmentName || 'N/A' }}</span>
                  </div>
                  <div class="info-item">
                    <i class="bx bx-briefcase"></i>
                    <span :class="getEmploymentTypeClass(employee.employmentType)">
                      {{ employee.employmentType || 'N/A' }}
                    </span>
                  </div>
                </div>
                
                <div class="employee-status">
                  <span :class="getStatusBadgeClass(employee.status)">
                    {{ getStatusText(employee.status) }}
                  </span>
                  <small class="text-muted">
                    {{ calculateTenure(employee.hireDate) }} tenure
                  </small>
                </div>
              </div>
            </div>
          </div>

          <!-- Table View -->
          <div v-else class="employees-table">
            <div class="table-responsive">
              <table class="table">
                <thead>
                  <tr>
                    <th width="50">
                      <input type="checkbox" v-model="selectAll" @change="toggleSelectAll">
                    </th>
                    <th>Employee</th>
                    <th>Contact</th>
                    <th>Department</th>
                    <th>Employment</th>
                    <th>Status</th>
                    <th class="text-center">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="employee in employees" :key="employee.id" 
                      :class="{ 'selected': selectedEmployees.includes(employee.id) }">
                    <td>
                      <input type="checkbox" :value="employee.id" v-model="selectedEmployees">
                    </td>
                    <td>
                      <div class="employee-cell">
                        <div class="avatar">
                          {{ getInitials(employee.firstName, employee.lastName) }}
                        </div>
                        <div>
                          <h6>{{ getFullName(employee) }}</h6>
                          <small>{{ displayEmpNo(employee) }}</small>
                          <small v-if="employee.nationalId" class="privacy-meta privacy-meta-stack">{{ displayNationalId(employee) }}</small>
                          <small v-if="employee.dateOfBirth" class="privacy-meta privacy-meta-stack">{{ displayDateOfBirth(employee) }}</small>
                        </div>
                      </div>
                    </td>
                    <td>
                      <div>
                        <div><i class="bx bx-envelope"></i> {{ displayEmail(employee) }}</div>
                        <div><i class="bx bx-phone"></i> {{ displayPhone(employee) }}</div>
                      </div>
                    </td>
                    <td>
                      <span class="dept-badge">{{ employee.departmentName || 'N/A' }}</span>
                    </td>
                    <td>
                      <div>
                        <span :class="getEmploymentTypeClass(employee.employmentType)">
                          {{ employee.employmentType || 'N/A' }}
                        </span>
                        <br>
                        <small>{{ formatDate(employee.hireDate) }}</small>
                      </div>
                    </td>
                    <td>
                      <span :class="getStatusBadgeClass(employee.status)">
                        {{ getStatusText(employee.status) }}
                      </span>
                    </td>
                    <td class="text-center">
                      <div class="btn-group">
                        <button @click="viewDetails(employee)" class="btn btn-sm btn-icon">
                          <i class="bx bx-show"></i>
                        </button>
                        <button @click="editEmployee(employee)" class="btn btn-sm btn-icon">
                          <i class="bx bx-edit"></i>
                        </button>
                        <div class="dropdown">
                          <button class="btn btn-sm btn-icon dropdown-toggle">
                            <i class="bx bx-dots-vertical-rounded"></i>
                          </button>
                          <div class="dropdown-menu">
                            <a @click="viewUserAccount(employee)" href="javascript:void(0)" class="dropdown-item">
                              <i class="bx bx-user-circle"></i> Manage Account
                            </a>
                            <a href="javascript:void(0)" class="dropdown-item">
                              <i class="bx bx-file"></i> Generate Report
                            </a>
                            <div class="dropdown-divider"></div>
                            <a @click="deleteEmployee(employee)" href="javascript:void(0)" class="dropdown-item text-danger">
                              <i class="bx bx-trash"></i> Delete
                            </a>
                          </div>
                        </div>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <!-- Pagination -->
          <div class="pagination-section" v-if="employees.length > 0">
            <div class="pagination-info">
              Showing {{ startIndex + 1 }} to {{ Math.min(endIndex, totalItems) }} of {{ totalItems }} entries
            </div>
            <div class="pagination-controls">
              <button @click="prevPage" :disabled="currentPage === 1" class="pagination-btn">
                <i class="bx bx-chevron-left"></i>
              </button>
              
              <template v-for="page in visiblePages" :key="page">
                <button v-if="page === '...'" class="pagination-btn disabled">...</button>
                <button v-else @click="goToPage(page)" 
                        :class="['pagination-btn', { 'active': page === currentPage }]">
                  {{ page }}
                </button>
              </template>
              
              <button @click="nextPage" :disabled="currentPage === totalPages || totalPages === 0" class="pagination-btn">
                <i class="bx bx-chevron-right"></i>
              </button>
            </div>
          </div>
        </div>

        <!-- Bulk Actions Bar -->
        <div v-if="selectedEmployees.length > 0" class="bulk-actions-bar">
          <div class="bulk-actions-content">
            <span class="selected-count">
              <i class="bx bx-check-circle"></i>
              {{ selectedEmployees.length }} employee(s) selected
            </span>
            <div class="bulk-actions">
              <button class="bulk-btn">
                <i class="bx bx-envelope"></i> Send Email
              </button>
              <button class="bulk-btn">
                <i class="bx bx-export"></i> Export Selected
              </button>
              <button class="bulk-btn warning">
                <i class="bx bx-edit"></i> Bulk Edit
              </button>
              <button @click="deleteSelected" class="bulk-btn danger">
                <i class="bx bx-trash"></i> Delete Selected
              </button>
            </div>
          </div>
        </div>

      </div>
    </div>

    <!-- Modals -->
    <UserManagementModal
      v-if="showUserModal"
      :employee="selectedEmployee"
      :reveal-sensitive="sensitiveDataVisible"
      @close="showUserModal = false"
    />

    <DirectOnboardingModal
      v-if="showDirectOnboardingModal"
      :departments="departments"
      @close="showDirectOnboardingModal = false"
    />
  </div>
</template>

<script>
/* eslint-disable */
import Navigation from "@/components/Navigation.vue";
import UserManagementModal from "@/components/employeeRegister/modals/UserManagementModal.vue";
import DirectOnboardingModal from "@/components/employeeRegister/modals/DirectOnboardingModal.vue";
import { authUrl, execute, toast } from "@/api";
import moment from "moment";
import {
  maskDateOfBirth,
  maskEmail,
  maskEmpNo,
  maskNationalId,
  maskPhone
} from "@/utils/privacyMask";

export default {
  name: "EmployeeRegister",
  components: {
    Navigation,
    UserManagementModal,
    DirectOnboardingModal
  },
  data() {
    return {
      isNavCollapsed: false,
      loading: false,
      isDownloading: false,
      showUserModal: false,
      showDirectOnboardingModal: false,
      viewMode: 'list', // 'grid' or 'list'
      
      // Filters
      search: '',
      departmentFilter: '',
      statusFilter: '',
      typeFilter: '',
      
      // Pagination
      currentPage: 1,
      totalPages: 0,
      pageSize: 20,
      
      // Data
      employees: [],
      departments: [],
      totalItems: 0,
      
      // Stats
      activeEmployees: 0,
      resignedEmployees: 0,
      permanentEmployees: 0,
      contractEmployees: 0,
      internshipEmployees: 0,
      newEmployeesThisMonth: 0,
      averageTenure: 0,
      
      // Selection
      selectedEmployees: [],
      selectAll: false,
      selectedEmployee: null,
      
      // Utilities
      searchTimeout: null,
      currentYear: new Date().getFullYear(),

      /** When false, payroll no., national ID, DOB, email and phone are masked in the register list. */
      sensitiveDataVisible: false
    };
  },
  computed: {
    startIndex() {
      return (this.currentPage - 1) * this.pageSize;
    },
    endIndex() {
      return Math.min(this.currentPage * this.pageSize, this.totalItems);
    },
    visiblePages() {
      const pages = [];
      const total = this.totalPages;
      const current = this.currentPage;
      const maxVisible = 5;

      if (total <= maxVisible) {
        for (let i = 1; i <= total; i++) pages.push(i);
        return pages;
      }

      let start = Math.max(current - Math.floor(maxVisible / 2), 1);
      let end = start + maxVisible - 1;

      if (end > total) {
        end = total;
        start = end - maxVisible + 1;
      }

      if (start > 1) {
        pages.push(1);
        if (start > 2) pages.push('...');
      }

      for (let i = start; i <= end; i++) pages.push(i);

      if (end < total) {
        if (end < total - 1) pages.push('...');
        pages.push(total);
      }

      return pages;
    }
  },
  mounted() {
    try {
      this.sensitiveDataVisible = sessionStorage.getItem("staffsyncEmployeeRegisterRevealSensitive") === "1";
    } catch {
      void 0;
    }
    this.initializeData();
  },
  methods: {
    openDirectOnboarding() {
      this.showDirectOnboardingModal = true;
    },
    toggleSensitiveReveal() {
      this.sensitiveDataVisible = !this.sensitiveDataVisible;
      try {
        sessionStorage.setItem(
          "staffsyncEmployeeRegisterRevealSensitive",
          this.sensitiveDataVisible ? "1" : "0"
        );
      } catch {
        void 0;
      }
    },

    displayEmpNo(employee) {
      const v = employee?.empNo;
      if (!v) return "N/A";
      return this.sensitiveDataVisible ? String(v).trim() : maskEmpNo(v) || "N/A";
    },

    displayNationalId(employee) {
      const v = employee?.nationalId;
      if (!v) return "";
      return this.sensitiveDataVisible ? String(v).trim() : maskNationalId(v);
    },

    displayDateOfBirth(employee) {
      const v = employee?.dateOfBirth;
      if (!v) return "";
      return this.sensitiveDataVisible
        ? moment(v).format("DD MMM YYYY")
        : maskDateOfBirth(v, moment);
    },

    displayEmail(employee) {
      const v = employee?.email;
      if (!v) return "N/A";
      return this.sensitiveDataVisible ? String(v).trim() : maskEmail(v) || "N/A";
    },

    displayPhone(employee) {
      const v = employee?.phone;
      if (!v) return "N/A";
      return this.sensitiveDataVisible ? this.formatPhone(v) : maskPhone(v) || "N/A";
    },

    // Navigation
    handleNavToggle(isCollapsed) {
      this.isNavCollapsed = isCollapsed;
    },
    
    goToHome() {
      this.$router.push('/dashboard');
    },
    
    goToCreateEmployee() {
      this.$router.push('/employee-create');
    },
    
    goToBulkUpload() {
      this.$router.push('/employee-bulk-upload');
    },

    goToArchive() {
      this.$router.push({ name: 'employee-archive' });
    },
    
    // Data initialization
    initializeData() {
      this.getDepartments();
      this.getEmployees();
    },
    
    refreshData() {
      this.getDepartments();
      this.getEmployees();
    },
    
    // Data fetching
    getDepartments() {
      const data = new FormData();
      data.append("function", "getDepartments");

      execute(data, authUrl)
        .then((res) => {
          if (res.data.success) {
            this.departments = res.data.data.departments || [];
          }
        })
        .catch((e) => {
          console.error("Error fetching departments:", e);
        });
    },
    
    getEmployees() {
      this.loading = true;
      this.selectedEmployees = [];
      this.selectAll = false;

      const data = new FormData();
      data.append("function", "getEmployees");
      data.append("page", this.currentPage);
      data.append("rows_per_page", this.pageSize);
      data.append("keyword", this.search);
      data.append("archived", "0");

      if (this.departmentFilter) {
        data.append("department", this.departmentFilter);
      }
      if (this.statusFilter) {
        data.append("status", this.statusFilter);
      }
      if (this.typeFilter) {
        data.append("employmentType", this.typeFilter);
      }

      execute(data, authUrl)
        .then((res) => {
          this.loading = false;
          if (res.data.success) {
            this.employees = res.data.data.employees || [];
            this.totalItems = res.data.data.totalItems || 0;
            this.totalPages = Math.ceil(this.totalItems / this.pageSize);
            this.calculateStats();
          }
        })
        .catch((e) => {
          this.loading = false;
          console.error("Error fetching employees:", e);
        });
    },
    
    calculateStats() {
      this.activeEmployees = 0;
      this.resignedEmployees = 0;
      this.permanentEmployees = 0;
      this.contractEmployees = 0;
      this.internshipEmployees = 0;
      this.newEmployeesThisMonth = 0;
      
      let totalTenure = 0;
      let tenureCount = 0;
      const currentMonth = moment().format('YYYY-MM');

      this.employees.forEach(emp => {
        if (emp.status?.toLowerCase() === 'active') {
          this.activeEmployees++;
        }
        if (emp.status?.toLowerCase() === 'resigned') {
          this.resignedEmployees++;
        }

        if (emp.employmentType === 'Permanent') {
          this.permanentEmployees++;
        } else if (emp.employmentType === 'Contract') {
          this.contractEmployees++;
        } else if (emp.employmentType === 'Internship') {
          this.internshipEmployees++;
        }

        if (emp.hireDate && moment(emp.hireDate).format('YYYY-MM') === currentMonth) {
          this.newEmployeesThisMonth++;
        }

        if (emp.hireDate && emp.status?.toLowerCase() === 'active') {
          const tenure = moment().diff(moment(emp.hireDate), 'years', true);
          totalTenure += tenure;
          tenureCount++;
        }
      });

      this.averageTenure = tenureCount > 0 ? (totalTenure / tenureCount).toFixed(1) : 0;
    },
    
    // Search and filters
    debounceSearch() {
      clearTimeout(this.searchTimeout);
      this.searchTimeout = setTimeout(() => {
        this.currentPage = 1;
        this.getEmployees();
      }, 500);
    },
    
    clearSearch() {
      this.search = '';
      this.currentPage = 1;
      this.getEmployees();
    },
    
    onFilterChange() {
      this.currentPage = 1;
      this.getEmployees();
    },
    
    resetFilters() {
      this.search = '';
      this.departmentFilter = '';
      this.statusFilter = '';
      this.typeFilter = '';
      this.currentPage = 1;
      this.getEmployees();
    },
    
    // Navigation helpers
    filterByType(type) {
      this.typeFilter = type;
      this.currentPage = 1;
      this.getEmployees();
    },
    
    filterByStatus(status) {
      this.statusFilter = status;
      this.currentPage = 1;
      this.getEmployees();
    },
    
    filterResigned() {
      this.statusFilter = 'resigned';
      this.currentPage = 1;
      this.getEmployees();
    },
    
    // Employee actions
    viewDetails(employee) {
      sessionStorage.setItem("employeeId", employee.id);
      this.$router.push('/employee-details-view');
    },
    
    editEmployee(employee) {
      sessionStorage.setItem("employeeId", employee.id);
      this.$router.push('/employee-edit');
    },
    
    viewUserAccount(employee) {
      this.selectedEmployee = employee;
      this.showUserModal = true;
    },
    
    deleteEmployee(employee) {
      const confirmDelete = window.confirm(
        `Move ${this.getFullName(employee)} to the employee archive?\n\n` +
        `They will be removed from the active register and their login will be deactivated.`
      );

      if (!confirmDelete) return;

      const data = new FormData();
      data.append("function", "deleteEmployee");
      data.append("employeeId", employee.id);
      data.append("reason", "Deleted via employee register");

      execute(data, authUrl)
        .then((res) => {
          if (res.data.success) {
            // Show success message
            toast.success(`${this.getFullName(employee)} has been deleted successfully`);
            // Refresh the employee list
            this.getEmployees();
          } else {
            toast.error(res.data.message || 'Failed to delete employee');
          }
        })
        .catch((e) => {
          console.error("Error deleting employee:", e);
          toast.error('An error occurred while deleting the employee');
        });
    },
    
    deleteSelected() {
      if (this.selectedEmployees.length === 0) {
        toast.warning('No employees selected for deletion');
        return;
      }

      const confirmDelete = window.confirm(
        `Are you sure you want to delete ${this.selectedEmployees.length} employee(s)?\n\n` +
        `This action will mark the employees as terminated but can be recovered if needed.`
      );

      if (!confirmDelete) return;

      this.loading = true;
      let deletedCount = 0;
      let failedCount = 0;
      const employeeIds = [...this.selectedEmployees];

      // Delete employees one by one
      const deleteNext = () => {
        if (employeeIds.length === 0) {
          this.loading = false;
          this.clearSelection();
          if (deletedCount > 0) {
            toast.success(`${deletedCount} employee(s) deleted successfully`);
          }
          if (failedCount > 0) {
            toast.warning(`${failedCount} employee(s) failed to delete`);
          }
          this.getEmployees();
          return;
        }

        const employeeId = employeeIds.shift();
        const data = new FormData();
        data.append("function", "deleteEmployee");
        data.append("employeeId", employeeId);
        data.append("reason", "Deleted via bulk delete");

        execute(data, authUrl)
          .then((res) => {
            if (res.data.success) {
              deletedCount++;
            } else {
              failedCount++;
            }
            deleteNext();
          })
          .catch((e) => {
            console.error("Error deleting employee:", e);
            failedCount++;
            deleteNext();
          });
      };

      deleteNext();
    },
    
    // Selection
    toggleSelectAll() {
      if (this.selectAll) {
        this.selectedEmployees = this.employees.map(emp => emp.id);
      } else {
        this.selectedEmployees = [];
      }
    },
    
    clearSelection() {
      this.selectedEmployees = [];
      this.selectAll = false;
    },
    
    // Pagination
    prevPage() {
      if (this.currentPage > 1) {
        this.currentPage--;
        this.getEmployees();
      }
    },
    
    nextPage() {
      if (this.currentPage < this.totalPages) {
        this.currentPage++;
        this.getEmployees();
      }
    },
    
    goToPage(page) {
      if (typeof page === 'number' && page > 0 && page <= this.totalPages && page !== this.currentPage) {
        this.currentPage = page;
        this.getEmployees();
      }
    },
    
    // Export
    downloadEmployees() {
      if (this.isDownloading || this.employees.length === 0) return;

      this.isDownloading = true;
      // Add your export logic here
      setTimeout(() => {
        this.isDownloading = false;
      }, 1000);
    },
    
    exportData() {
      this.downloadEmployees();
    },
    
    // UI helpers
    getInitials(firstName, lastName) {
      return ((firstName ? firstName.charAt(0) : '') + 
              (lastName ? lastName.charAt(0) : '')).toUpperCase();
    },
    
    getFullName(employee) {
      const parts = [employee.firstName, employee.middleName, employee.lastName].filter(Boolean);
      return parts.join(' ') || 'N/A';
    },
    
    formatPhone(phone) {
      if (!phone) return 'N/A';
      return phone;
    },
    
    formatNumber(num) {
      return num?.toLocaleString() || '0';
    },
    
    formatDate(date) {
      if (!date) return 'N/A';
      return moment(date).format('DD MMM YYYY');
    },
    
    calculateTenure(hireDate) {
      if (!hireDate) return 'N/A';
      const years = moment().diff(moment(hireDate), 'years');
      const months = moment().diff(moment(hireDate), 'months') % 12;

      if (years > 0) {
        return `${years} year${years > 1 ? 's' : ''} ${months > 0 ? months + ' mo' : ''}`;
      } else if (months > 0) {
        return `${months} month${months > 1 ? 's' : ''}`;
      } else {
        return 'New hire';
      }
    },
    
    getStatusClass(status) {
      return status?.toLowerCase() === 'active' ? 'active' : 'inactive';
    },
    
    getStatusBadgeClass(status) {
      const statusLower = status?.toLowerCase();
      if (statusLower === 'active') return 'status-badge active';
      if (statusLower === 'resigned') return 'status-badge resigned';
      return 'status-badge unknown';
    },
    
    getStatusText(status) {
      if (!status) return 'Unknown';
      return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
    },
    
    getEmploymentTypeClass(type) {
      const classes = {
        'Permanent': 'badge success',
        'Contract': 'badge warning',
        'Temporary': 'badge info',
        'Internship': 'badge secondary'
      };
      return classes[type] || 'badge secondary';
    },
    
    getTopDepartments(count) {
      return this.departments.slice(0, count).map(d => d.departmentName).join(', ');
    },
    
    // UI Actions
    showDepartmentsModal() {
      // Implement department modal
      console.log('Show departments modal');
    },
    
    showHelp() {
      // Implement help modal
      console.log('Show help');
    },
    
    showSettings() {
      this.$router.push('/settings');
    }
  }
};
</script>

<style scoped>
/* Main Layout */
.employee-register {
  min-height: 100vh;
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
}

.employee-register.nav-collapsed .dashboard-content {
  margin-left: 70px;
}

.dashboard-content {
  margin-left: 280px;
  padding: 20px;
  transition: margin-left 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.page-container {
  max-width: 1600px;
  margin: 0 auto;
}

/* Modern Page Header */
.modern-page-header {
  background: #ffffff;
  border-radius: 20px;
  padding: 25px 30px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  margin-bottom: 30px;
  border: 1px solid rgba(226, 232, 240, 0.8);
  position: relative;
  overflow: visible;
}

.modern-page-header::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
  background: linear-gradient(90deg, #4f46e5 0%, #7c3aed 100%);
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  flex-wrap: wrap;
  gap: 20px;
  margin-bottom: 15px;
}

.title-section {
  flex: 1 1 auto;
  min-width: 0;
  max-width: 100%;
}

.dashboard-title {
  font-size: clamp(1.25rem, 4vw, 32px);
  font-weight: 800;
  color: #1e293b;
  margin: 0;
  line-height: 1.2;
  word-wrap: break-word;
  background: linear-gradient(90deg, #1e293b 0%, #475569 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.dashboard-subtitle {
  color: #64748b;
  font-size: 16px;
  margin: 10px 0 0 0;
  display: flex;
  align-items: center;
  gap: 8px;
}

.dashboard-subtitle .icon {
  font-size: 18px;
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 15px;
}

.view-selector {
  display: flex;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  overflow: hidden;
}

.view-btn {
  padding: 10px 20px;
  background: none;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  color: #64748b;
  font-weight: 600;
  transition: all 0.2s;
}

.view-btn:hover {
  background: #e2e8f0;
  color: #4f46e5;
}

.view-btn.active {
  background: #4f46e5;
  color: white;
}

.refresh-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  background: linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%);
  color: white;
  border: none;
  border-radius: 12px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
}

.refresh-btn:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(79, 70, 229, 0.3);
}

.refresh-btn:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.refresh-btn i.spin {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

.breadcrumb-section {
  padding-top: 15px;
  border-top: 1px solid #e2e8f0;
}

.breadcrumb {
  display: flex;
  align-items: center;
  gap: 10px;
  color: #475569;
  font-size: 14px;
  font-weight: 500;
}

.breadcrumb-item {
  display: flex;
  align-items: center;
  gap: 6px;
  color: #475569;
  text-decoration: none;
  cursor: pointer;
  transition: color 0.2s;
}

.breadcrumb-item:hover {
  color: #4f46e5;
}

.breadcrumb-current {
  color: #94a3b8;
  font-weight: 600;
}

.breadcrumb-separator {
  color: #cbd5e1;
}

/* Metrics Grid */
.metrics-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 20px;
  margin-bottom: 30px;
}

.metric-card {
  position: relative;
  border-radius: 20px;
  padding: 25px;
  overflow: hidden;
  border: 1px solid rgba(226, 232, 240, 0.8);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  transition: all 0.3s ease;
  background: #ffffff;
  min-height: 160px;
  cursor: pointer;
}

.metric-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.12);
}

.gradient-1 {
  background: linear-gradient(135deg, #dbeafe 0%, #eff6ff 100%);
  border-left: 4px solid #3b82f6;
}

.gradient-2 {
  background: linear-gradient(135deg, #d1fae5 0%, #f0fdf4 100%);
  border-left: 4px solid #10b981;
}

.gradient-3 {
  background: linear-gradient(135deg, #fef3c7 0%, #fefce8 100%);
  border-left: 4px solid #f59e0b;
}

.gradient-4 {
  background: linear-gradient(135deg, #f3e8ff 0%, #faf5ff 100%);
  border-left: 4px solid #8b5cf6;
}

.metric-icon {
  position: absolute;
  top: 25px;
  right: 25px;
  width: 60px;
  height: 60px;
  background: white;
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #e2e8f0;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
}

.metric-icon i {
  font-size: 28px;
}

.gradient-1 .metric-icon i { color: #3b82f6; }
.gradient-2 .metric-icon i { color: #10b981; }
.gradient-3 .metric-icon i { color: #f59e0b; }
.gradient-4 .metric-icon i { color: #8b5cf6; }

.metric-content {
  padding-right: 70px;
}

.metric-label {
  color: #64748b;
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 12px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.metric-value {
  color: #1e293b;
  font-size: 36px;
  font-weight: 800;
  margin: 0;
  line-height: 1;
}

.metric-footer {
  margin-top: 15px;
  display: flex;
  align-items: center;
  gap: 10px;
}

.trend-indicator {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: 12px;
  font-size: 13px;
  font-weight: 600;
}

.trend-indicator.positive {
  background: rgba(16, 185, 129, 0.1);
  color: #065f46;
}

.trend-indicator.neutral {
  background: rgba(100, 116, 139, 0.1);
  color: #475569;
}

.trend-badge {
  display: inline-flex;
  padding: 6px 12px;
  border-radius: 12px;
  font-size: 13px;
  font-weight: 600;
}

.trend-badge.active {
  background: rgba(16, 185, 129, 0.1);
  color: #065f46;
}

.trend-badge.permanent {
  background: rgba(245, 158, 11, 0.1);
  color: #92400e;
}

/* Quick Stats */
.quick-stats {
  background: white;
  border-radius: 20px;
  padding: 20px;
  margin-bottom: 30px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
}

.stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 20px;
}

.stat-item {
  display: flex;
  align-items: center;
  gap: 15px;
  padding: 15px;
  border-radius: 12px;
  background: #f8fafc;
  cursor: pointer;
  transition: all 0.2s;
}

.stat-item:hover {
  background: #e2e8f0;
  transform: translateY(-2px);
}

.stat-icon {
  width: 50px;
  height: 50px;
  border-radius: 12px;
  background: linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 24px;
}

.stat-content h4 {
  margin: 0;
  font-size: 24px;
  font-weight: 700;
  color: #1e293b;
}

.stat-content p {
  margin: 5px 0 0 0;
  color: #64748b;
  font-size: 14px;
}

/* Filters Section */
.filters-section {
  background: white;
  border-radius: 20px;
  padding: 25px;
  margin-bottom: 30px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
}

.filters-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.filters-header-actions {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  justify-content: flex-end;
}

.privacy-toggle-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 14px;
  border-radius: 12px;
  border: 1px solid #cbd5e1;
  background: #fff;
  color: #475569;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: border-color 0.2s, background 0.2s, color 0.2s;
}

.privacy-toggle-btn:hover {
  border-color: #64748b;
  color: #1e293b;
}

.privacy-toggle-btn.active {
  border-color: #4f46e5;
  background: #eef2ff;
  color: #4338ca;
}

.privacy-meta {
  font-size: 12px;
  color: #64748b;
  margin: 4px 0 0;
  display: flex;
  align-items: center;
  gap: 6px;
}

.privacy-meta-stack {
  display: block !important;
  margin-top: 2px;
}

.filters-header h3 {
  margin: 0;
  font-size: 18px;
  color: #1e293b;
  display: flex;
  align-items: center;
  gap: 8px;
}

.selected-count {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  background: #e0f2fe;
  color: #0369a1;
  border-radius: 12px;
  font-size: 14px;
  font-weight: 600;
}

.clear-selection {
  background: none;
  border: none;
  color: #0369a1;
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
}

.filters-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 15px;
  margin-bottom: 20px;
}

.search-box {
  position: relative;
  grid-column: 1 / -1;
}

.search-box i {
  position: absolute;
  left: 15px;
  top: 50%;
  transform: translateY(-50%);
  color: #64748b;
}

.search-box input {
  width: 100%;
  padding: 12px 45px 12px 45px;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  font-size: 14px;
  transition: all 0.2s;
}

.search-box input:focus {
  outline: none;
  border-color: #4f46e5;
  box-shadow: 0 0 0 3px rgba(79, 70, 229, 0.1);
}

.search-box input.is-loading {
  background-color: #f8f9fa;
}

.clear-btn {
  position: absolute;
  right: 15px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: #dc3545;
  cursor: pointer;
  padding: 0;
}

.filter-group label {
  display: block;
  margin-bottom: 8px;
  color: #475569;
  font-size: 14px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 6px;
}

.form-select {
  width: 100%;
  padding: 12px;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  background: white;
  font-size: 14px;
  cursor: pointer;
}

.action-buttons {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}

.action-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  border: none;
  border-radius: 12px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
}

.action-btn:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.action-btn.export {
  background: #f1f5f9;
  color: #475569;
  border: 1px solid #e2e8f0;
}

.action-btn.upload {
  background: #f0f9ff;
  color: #0369a1;
  border: 1px solid #bae6fd;
}

.action-btn.primary {
  background: linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%);
  color: white;
}

.action-btn:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

/* Employees Section */
.employees-section {
  background: white;
  border-radius: 20px;
  padding: 25px;
  margin-bottom: 30px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
}

.loading-state {
  text-align: center;
  padding: 60px 20px;
}

.loading-state .spinner {
  width: 40px;
  height: 40px;
  border: 3px solid rgba(79, 70, 229, 0.1);
  border-top-color: #4f46e5;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 20px;
}

.empty-state {
  text-align: center;
  padding: 60px 20px;
}

.empty-icon {
  width: 80px;
  height: 80px;
  background: #f1f5f9;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 20px;
  color: #94a3b8;
  font-size: 32px;
}

.empty-state h3 {
  margin: 0 0 10px 0;
  color: #1e293b;
}

.empty-state p {
  color: #64748b;
  margin: 0 0 20px 0;
}

.btn-outline {
  padding: 10px 24px;
  background: white;
  border: 1px solid #4f46e5;
  color: #4f46e5;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 600;
  transition: all 0.2s;
}

.btn-outline:hover {
  background: #4f46e5;
  color: white;
}

/* Grid View */
.employees-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 20px;
}

.employee-card {
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  overflow: visible;
  transition: all 0.3s ease;
  position: relative;
  z-index: 0;
}
.employee-card:hover {
  z-index: 10;
  transform: translateY(-5px);
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.12);
}


.card-header {
  background: #f8fafc;
  padding: 20px;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
}

.employee-avatar {
  width: 60px;
  height: 60px;
  border-radius: 12px;
  background: linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  font-weight: 700;
}

.employee-avatar.active {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
}

.employee-avatar.inactive {
  background: linear-gradient(135deg, #64748b 0%, #475569 100%);
}

.employee-actions {
  display: flex;
  gap: 5px;
}

.icon-btn {
  width: 36px;
  height: 36px;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
  background: white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #64748b;
  transition: all 0.2s;
}

.icon-btn:hover {
  background: #f1f5f9;
  color: #4f46e5;
}

.dropdown {
  position: relative;
}

.dropdown-menu {
  position: absolute;
  top: 100%;
  right: 0;
  margin-top: 4px;
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  min-width: 180px;
  z-index: 1050;
  display: none;
}

.dropdown:hover .dropdown-menu {
  display: block;
}

.dropdown-item {
  padding: 10px 15px;
  display: flex;
  align-items: center;
  gap: 8px;
  color: #475569;
  text-decoration: none;
  cursor: pointer;
  transition: all 0.2s;
}

.dropdown-item:hover {
  background: #f1f5f9;
  color: #4f46e5;
}

.dropdown-divider {
  height: 1px;
  background: #e2e8f0;
  margin: 5px 0;
}

.card-body {
  padding: 20px;
}

.card-body h4 {
  margin: 0 0 5px 0;
  color: #1e293b;
}

.employee-id {
  color: #64748b;
  font-size: 14px;
  margin: 0 0 15px 0;
}

.employee-info {
  margin-bottom: 15px;
}

.info-item {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 8px;
  font-size: 14px;
  color: #475569;
}

.info-item i {
  color: #64748b;
  width: 20px;
}

.employee-status {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 15px;
  border-top: 1px solid #e2e8f0;
}

.status-badge {
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
}

.status-badge.active {
  background: rgba(16, 185, 129, 0.1);
  color: #065f46;
}

.status-badge.resigned {
  background: rgba(239, 68, 68, 0.1);
  color: #991b1b;
}

.status-badge.unknown {
  background: rgba(100, 116, 139, 0.1);
  color: #475569;
}

/* Table View */
.employees-table {
  overflow-x: auto;
}

.table {
  width: 100%;
  border-collapse: collapse;
}

.table td.text-center {
  position: relative;
  overflow: visible;
}

.table tbody tr {
  position: relative;
}

.table th {
  padding: 15px;
  text-align: left;
  font-weight: 600;
  color: #475569;
  background: #f8fafc;
  border-bottom: 2px solid #e2e8f0;
}

.table td {
  padding: 15px;
  border-bottom: 1px solid #e2e8f0;
}

.table tbody tr:hover {
  background: #f8fafc;
}

.table tbody tr.selected {
  background: #e0f2fe;
}

.employee-cell {
  display: flex;
  align-items: center;
  gap: 12px;
}

.avatar {
  width: 40px;
  height: 40px;
  border-radius: 8px;
  background: linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
}

.dept-badge {
  padding: 4px 12px;
  background: #e0f2fe;
  color: #0369a1;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
}

.badge {
  padding: 4px 8px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
}

.badge.success {
  background: rgba(16, 185, 129, 0.1);
  color: #065f46;
}

.badge.warning {
  background: rgba(245, 158, 11, 0.1);
  color: #92400e;
}

.badge.info {
  background: rgba(59, 130, 246, 0.1);
  color: #1e40af;
}

.badge.secondary {
  background: rgba(100, 116, 139, 0.1);
  color: #475569;
}

.btn-group {
  display: flex;
  gap: 5px;
}

.btn-sm {
  padding: 6px;
}

.btn-icon {
  width: 32px;
  height: 32px;
  border-radius: 6px;
  border: 1px solid #e2e8f0;
  background: white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #64748b;
  transition: all 0.2s;
}

.btn-icon:hover {
  background: #f1f5f9;
  color: #4f46e5;
}

/* Pagination */
.pagination-section {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 30px;
  padding-top: 20px;
  border-top: 1px solid #e2e8f0;
}

.pagination-info {
  color: #64748b;
  font-size: 14px;
}

.pagination-controls {
  display: flex;
  gap: 5px;
}

.pagination-btn {
  width: 36px;
  height: 36px;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
  background: white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #475569;
  transition: all 0.2s;
}

.pagination-btn:hover:not(:disabled) {
  background: #f1f5f9;
  color: #4f46e5;
}

.pagination-btn.active {
  background: #4f46e5;
  color: white;
  border-color: #4f46e5;
}

.pagination-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.pagination-btn.disabled {
  border: none;
  background: none;
  cursor: default;
}

/* Bulk Actions Bar */
.bulk-actions-bar {
  position: fixed;
  bottom: 20px;
  left: 50%;
  transform: translateX(-50%);
  background: white;
  border-radius: 16px;
  padding: 15px 25px;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.2);
  z-index: 1000;
  min-width: 600px;
}

.bulk-actions-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.selected-count {
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 600;
  color: #4f46e5;
}

.bulk-actions {
  display: flex;
  gap: 8px;
}

.bulk-btn {
  padding: 8px 16px;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: all 0.2s;
}

.bulk-btn:first-child {
  background: #f1f5f9;
  color: #475569;
}

.bulk-btn:nth-child(2) {
  background: #f0f9ff;
  color: #0369a1;
}

.bulk-btn.warning {
  background: #fef3c7;
  color: #92400e;
}

.bulk-btn.danger {
  background: #fee2e2;
  color: #991b1b;
}

.bulk-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

/* Footer */
.modern-footer {
  background: #ffffff;
  border-top: 1px solid #e2e8f0;
  padding: 25px 0;
  margin-top: 40px;
  border-radius: 20px 20px 0 0;
  box-shadow: 0 -2px 12px rgba(0, 0, 0, 0.04);
}

.footer-content {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 15px;
}

.footer-left p,
.footer-right p {
  color: #64748b;
  font-size: 14px;
  margin: 0;
}

.system-info {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 8px;
  font-size: 13px;
}

.system-info i {
  color: #10b981;
}

.powered-by {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #94a3b8;
  font-style: italic;
}

.powered-by i {
  color: #4f46e5;
}

.footer-links {
  display: flex;
  gap: 20px;
  margin-top: 8px;
}

.footer-links a {
  color: #64748b;
  text-decoration: none;
  font-size: 13px;
  transition: color 0.2s;
}

.footer-links a:hover {
  color: #4f46e5;
}

/* Responsive Design */
@media (max-width: 1024px) {
  .dashboard-content {
    margin-left: 70px;
  }
  
  .employee-register.nav-collapsed .dashboard-content {
    margin-left: 70px;
  }
  
  .bulk-actions-bar {
    min-width: 90%;
  }
}

@media (max-width: 768px) {
  .dashboard-content {
    margin-left: 0;
    padding: 15px;
  }
  
  .employee-register.nav-collapsed .dashboard-content {
    margin-left: 0;
  }

  .modern-page-header {
    padding: 20px;
  }

  .header-content {
    flex-direction: column;
    align-items: stretch;
  }

  .dashboard-title {
    font-size: 24px;
  }

  .metrics-grid {
    grid-template-columns: 1fr;
  }

  .stats-grid {
    grid-template-columns: 1fr;
  }

  .filters-grid {
    grid-template-columns: 1fr;
  }

  .employees-grid {
    grid-template-columns: 1fr;
  }

  .bulk-actions-bar {
    position: static;
    transform: none;
    width: 100%;
    margin-top: 20px;
    border-radius: 16px;
  }

  .bulk-actions-content {
    flex-direction: column;
    gap: 15px;
  }

  .bulk-actions {
    flex-wrap: wrap;
    justify-content: center;
  }

  .pagination-section {
    flex-direction: column;
    gap: 15px;
    align-items: stretch;
  }

  .footer-content {
    flex-direction: column;
    text-align: center;
    gap: 20px;
  }
}

@media (max-width: 480px) {
  .dashboard-title {
    font-size: 20px;
  }

  .metric-content {
    padding-right: 60px;
  }

  .metric-icon {
    width: 50px;
    height: 50px;
  }

  .metric-icon i {
    font-size: 24px;
  }

  .action-buttons {
    flex-direction: column;
  }

  .action-btn {
    width: 100%;
    justify-content: center;
  }
}
</style>